import { translateLabel } from "@/i18n/messages";
import React, { useState } from "react";
import { PlusIcon, XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  emptySuggestEditForm,
  nextSuggestEditField,
  normalizeSuggestEditInput,
  SUGGEST_EDIT_OTHER,
  suggestEditFieldConfig,
  suggestEditFieldsForTarget,
  submitSuggestEdit,
  validateSuggestEditForm,
} from "@/lib/suggestEdit";


const valueFieldClass = "mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-[#CCFF00] focus:outline-none";

function CorrectionValueField({ targetType, change, index, updateChange }) {
  const config = suggestEditFieldConfig(targetType, change.field) || {};
  const input = config.input || "text";
  const common = {
    required: true,
    value: change.proposedValue,
    onChange: (event) => updateChange(index, "proposedValue", event.target.value),
    "data-testid": `suggest-edit-value-${index}`,
    className: valueFieldClass,
  };

  if (input === "textarea") {
    return (
      <textarea
        {...common}
        rows={3}
        placeholder={config.placeholder || "Enter the corrected value"}
      />
    );
  }

  if (input === "select" || input === "select-other") {
    const selected = (config.options || []).find((option) => option.value === change.proposedValue);
    return (
      <>
        <select {...common}>
          <option value="" disabled>{translateLabel("Choose a value…")}</option>
          {(config.options || [])
            .filter((option) => option.value !== "")
            .map((option) => (
              <option key={option.value} value={option.value}>{translateLabel(option.label)}</option>
            ))}
        </select>
        {(selected?.help || config.help) && (
          <p className="mt-1.5 text-[11px] leading-snug text-zinc-500">{selected?.help || config.help}</p>
        )}
        {input === "select-other" && change.proposedValue === SUGGEST_EDIT_OTHER && (
          <input
            required
            value={change.otherValue || ""}
            onChange={(event) => updateChange(index, "otherValue", event.target.value)}
            placeholder={config.placeholder || "Suggest a new value"}
            data-testid={`suggest-edit-value-${index}-other`}
            className={valueFieldClass}
          />
        )}
      </>
    );
  }

  return (
    <input
      {...common}
      type={input === "url" ? "url" : input === "number" ? "number" : "text"}
      min={config.min}
      step={config.step}
      placeholder={config.placeholder || "Enter the corrected value"}
    />
  );
}

export function SuggestEdit({ targetType, targetId, targetName, compact = false }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => emptySuggestEditForm(targetType));
  const [saving, setSaving] = useState(false);
  const editableFields = suggestEditFieldsForTarget(targetType);
  const usedFields = new Set(form.changes.map((change) => change.field).filter(Boolean));
  const canAddField = editableFields.some((field) => !usedFields.has(field.value));

  const updateChange = (index, key, value) => {
    setForm((current) => ({
      ...current,
      changes: current.changes.map((change, i) => i === index ? { ...change, [key]: value } : change),
    }));
  };

  const addChange = () => setForm((current) => {
    const field = nextSuggestEditField(targetType, current.changes);
    if (!field) return current;
    return {
      ...current,
      changes: [...current.changes, { field, proposedValue: "", otherValue: "" }],
    };
  });

  const removeChange = (index) => setForm((current) => ({
    ...current,
    changes: current.changes.filter((_, i) => i !== index),
  }));

  const submit = async (event) => {
    event.preventDefault();
    const input = normalizeSuggestEditInput({ targetType, targetId, form });
    const validation = validateSuggestEditForm(input);
    if (validation) {
      toast.error(validation);
      return;
    }

    setSaving(true);
    try {
      await submitSuggestEdit(input);
      toast.success(translateLabel("Correction submitted"), { description: "Thanks — it will be reviewed before any canonical data changes." });
      setForm(emptySuggestEditForm(targetType));
      setOpen(false);
    } catch (error) {
      toast.error(error.message || "Could not submit correction");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {compact ? (
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="suggest-edit-toggle"
            className="flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-white"
          >
            <PlusIcon size={14} /> Suggest edit
          </button>
        </DialogTrigger>
      ) : (
        <section className="mb-12 rounded-2xl border border-white/10 bg-[#121214] p-5" data-testid="suggest-edit-section">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="label-tiny text-[#CCFF00]">{translateLabel("Community correction")}</div>
              <h2 className="mt-1 font-display text-2xl font-bold uppercase text-white">{translateLabel("Suggest an edit")}</h2>
              <p className="mt-1 text-sm text-zinc-400">
                Spotted outdated or incorrect information for {targetName}? Send a correction without creating an account.
              </p>
            </div>
            <DialogTrigger asChild>
              <button
                type="button"
                data-testid="suggest-edit-toggle"
                className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold text-white hover:bg-white/10"
              >
                Suggest edit
              </button>
            </DialogTrigger>
          </div>
        </section>
      )}

      <DialogContent className="z-[1400] max-h-[90dvh] max-w-2xl overflow-y-auto border-white/10 bg-[#121214] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold uppercase">{translateLabel("Suggest an edit")}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Suggest a correction for {targetName}. Nothing changes until an admin reviews and approves it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-5" data-testid="suggest-edit-form">
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="label-tiny">{translateLabel("Proposed changes")}</span>
              <button
                type="button"
                onClick={addChange}
                disabled={!canAddField}
                className="flex items-center gap-1 text-xs font-bold text-[#CCFF00] disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="suggest-edit-add-change"
              >
                <PlusIcon size={14} /> Add field
              </button>
            </div>
            <div className="space-y-2">
              {form.changes.map((change, index) => (
                <div key={index} className="grid gap-2 md:grid-cols-[0.8fr_1.4fr_auto] md:items-end">
                  <label className="block">
                    <span className="label-tiny">{translateLabel("Field · required")}</span>
                    <select
                      required
                      value={change.field}
                      onChange={(event) => {
                        updateChange(index, "field", event.target.value);
                        updateChange(index, "proposedValue", "");
                        updateChange(index, "otherValue", "");
                      }}
                      data-testid={`suggest-edit-field-${index}`}
                      aria-label={`Editable field ${index + 1}`}
                      className="mt-2 w-full rounded-xl border border-white/10 bg-[#171719] px-3 py-2.5 text-sm text-white focus:border-[#CCFF00] focus:outline-none"
                    >
                      {editableFields.map((field) => {
                        const selectedElsewhere = form.changes.some((candidate, candidateIndex) =>
                          candidateIndex !== index && candidate.field === field.value
                        );
                        return (
                          <option key={field.value} value={field.value} disabled={selectedElsewhere}>
                            {translateLabel(field.label)}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                  <label className="block">
                    <span className="label-tiny">{suggestEditFieldConfig(targetType, change.field)?.label || "Correction value"} · required</span>
                    <CorrectionValueField
                      targetType={targetType}
                      change={change}
                      index={index}
                      updateChange={updateChange}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => removeChange(index)}
                    disabled={form.changes.length === 1}
                    aria-label={translateLabel("Remove proposed change")}
                    className="h-[42px] rounded-xl border border-white/10 px-3 text-zinc-400 hover:text-white disabled:opacity-30"
                  >
                    <XIcon size={17} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="label-tiny">{translateLabel("Why should this change? · required")}</span>
            <textarea
              rows={3}
              required
              maxLength={2000}
              value={form.reason}
              onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
              data-testid="suggest-edit-reason"
              placeholder={translateLabel("Explain what is incorrect or outdated.")}
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-[#CCFF00] focus:outline-none"
            />
          </label>

          <div className="grid gap-3 md:grid-cols-2">
            <label>
              <span className="label-tiny">{translateLabel("Source URL · optional")}</span>
              <input
                type="url"
                value={form.sourceUrl}
                onChange={(event) => setForm((current) => ({ ...current, sourceUrl: event.target.value }))}
                placeholder="https://..."
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-[#CCFF00] focus:outline-none"
              />
            </label>
            <label>
              <span className="label-tiny">{translateLabel("Source note · optional")}</span>
              <input
                value={form.sourceNote}
                onChange={(event) => setForm((current) => ({ ...current, sourceNote: event.target.value }))}
                placeholder={translateLabel("Where did you verify it?")}
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-[#CCFF00] focus:outline-none"
              />
            </label>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <label>
              <span className="label-tiny">{translateLabel("Your name · optional")}</span>
              <input
                value={form.contactName}
                onChange={(event) => setForm((current) => ({ ...current, contactName: event.target.value }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white focus:border-[#CCFF00] focus:outline-none"
              />
            </label>
            <label>
              <span className="label-tiny">{translateLabel("Email · optional")}</span>
              <input
                type="email"
                value={form.contactEmail}
                onChange={(event) => setForm((current) => ({ ...current, contactEmail: event.target.value }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white focus:border-[#CCFF00] focus:outline-none"
              />
            </label>
          </div>

          <p className="text-xs text-zinc-500">
            Suggestions are stored for review. They never change destination or trail data automatically.
          </p>

          <button
            type="submit"
            disabled={saving}
            data-testid="suggest-edit-submit"
            className="rounded-full bg-[#CCFF00] px-5 py-2.5 text-sm font-bold text-black disabled:opacity-50"
          >
            {saving ? "Submitting…" : "Submit correction"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
